import useRolePermission from "entities/auth/hooks/useRolePermission";
import { useGamesData } from "entities/game/hooks/useGamesData";
import { useSelectedGamesInModuleQuery } from "entities/game/queries/useSelectedGamesInModuleQuery";
import { Game } from "entities/game/types";
import { useMetadataTagsListQuery } from "entities/metadataTags/queries/useMetadataTagsListQuery";
import { useUploadContentBlocksImages } from "entities/modules/hooks/useUploadContentBlocksImages";
import { useUploadSingleImage } from "entities/modules/hooks/useUploadSingleImage";
import { useModuleSelectedStudiosQuery } from "entities/modules/queries/useModuleSelectedStudiosQuery";
import { useSelectedGamesData } from "entities/modules/stores/useSelectedGamesData";
import { useTagBlocksStore } from "entities/modules/stores/useTagBlocksStore";
import { GamesOrderType, ModuleGame, ModuleType } from "entities/modules/types";
import {
  defaultTopPrivacyTipsBlock,
  isOneCardBlockModuleLayout,
  isThreeCardBlockModuleTypeLayout,
} from "entities/modules/utils";
import { useStudiosListQuery } from "entities/studios/queries/useStudiosListQuery";
import { useTagsListQuery } from "entities/tags/queries/useTagsListQuery";
import { useThemeTagsListQuery } from "entities/themeTags/queries/useThemeTagListQuery";
import { sortBy } from "lodash";
import { useContentSchedule } from "modules/contentSchedule/context";
import {
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createContext } from "shared/utils/context";
import {
  getAlgoliaTagsFilters,
  serializeModuleTagsToBlocks,
} from "widgets/ModuleTagsWidget/utils";
import { useShallow } from "zustand/react/shallow";
import { useGeneralData } from "../stores/useGeneralData";
import { useStudioOrderingData } from "../stores/useStudioOrderingData";
import { useStudiosData } from "../stores/useStudiosData";
import {
  PAGE_ONE_CARD_BLOCKS,
  PAGE_THREE_CARDS_BLOCKS,
  MODULE_TOP_PRIVACY_TIPS_BLOCK,
} from "./constants";
import { ModuleDetailsTabs, ModuleDetailsType } from "./types";
import { formatStudios, formatSelectedGames } from "./utils";

const [Provider, useContentScheduleModuleDetails] =
  createContext<ModuleDetailsType>({
    name: "ContentScheduleModuleDetailsContext",
    hookName: "useContentScheduleModuleDetails",
    providerName: "ContentScheduleModuleDetailsProvider",
  });

const ContentScheduleModuleDetailsProvider: FC<PropsWithChildren> = (props) => {
  const { moduleDetailsDrawer } = useContentSchedule();
  const brandId = moduleDetailsDrawer.drawerDetails?.brandId ?? "";
  const pageId = moduleDetailsDrawer.drawerDetails?.pageId ?? "";
  const moduleData = moduleDetailsDrawer.drawerDetails;
  const moduleId = (moduleData?.id || moduleData || null) as string | null;
  const [selectedTab, setSelectedTab] = useState(ModuleDetailsTabs.General);
  const { allowed: hasReadAccess } = useRolePermission("brandsAndPages:read");
  const { allowed: hasReadTranslationsAccess } = useRolePermission(
    "brandsAndPages:translations:read",
  );

  const { tableData: allGames, isLoading: isLoadingAllGames } = useGamesData({
    isActive: ["true"],
    size: 1000,
  });
  const { data: tags = [], isLoading: isLoadingTags } = useTagsListQuery();
  const { data: metadataTags = [], isLoading: isLoadingMetadataTags } =
    useMetadataTagsListQuery();
  const { data: themeTags = [], isLoading: isLoadingThemeTags } =
    useThemeTagsListQuery();
  const { data: studios, isLoading: isLoadingStudios } = useStudiosListQuery();
  const initialRenderRef = useRef(false);

  const {
    setModuleName,
    setAdminNotes,
    setTranslations,
    setModuleType,
    setModuleLayout,
    setStudioFilterEnabled,
    clearGeneralData,
    setBaseUrl,
    setContentBlocks,
    setBonusObtainType,
    setRows,
    contentBlocks,
    setOneCardBlockImage,
    setThreeCardBlockImages,
    moduleLayout,
    setIconShown,
    setIcon,
    moduleType,
    topPrivacyTipsBlock,
    setTopPrivacyTipsBlock,
    setTopPrivacyTipBlockImage,
    setScheduledStartAt,
    setScheduledEndAt,
    setIsSchedulingEnabled,
  } = useGeneralData(
    useShallow((state) => ({
      contentBlocks: state.contentBlocks,
      moduleLayout: state.moduleLayout,
      setModuleName: state.setModuleName,
      setAdminNotes: state.setAdminNotes,
      setTranslations: state.setTranslations,
      setModuleType: state.setModuleType,
      setRows: state.setRows,
      setModuleLayout: state.setModuleLayout,
      setStudioFilterEnabled: state.setStudioFilterEnabled,
      setBaseUrl: state.setBaseUrl,
      setContentBlocks: state.setContentBlocks,
      setBonusObtainType: state.setBonusObtainType,
      clearGeneralData: state.clearGeneralData,
      setOneCardBlockImage: state.setOneCardBlockImage,
      setThreeCardBlockImages: state.setThreeCardBlockImages,
      setIconShown: state.setIconShown,
      setIcon: state.setIcon,
      moduleType: state.moduleType,
      topPrivacyTipsBlock: state.topPrivacyTipsBlock,
      setTopPrivacyTipsBlock: state.setTopPrivacyTipsBlock,
      setTopPrivacyTipBlockImage: state.setTopPrivacyTipBlockImage,
      setScheduledStartAt: state.setScheduledStartAt,
      isSchedulingEnabled: state.isSchedulingEnabled,
      setIsSchedulingEnabled: state.setIsSchedulingEnabled,
      setScheduledEndAt: state.setScheduledEndAt,
    })),
  );

  const { loadingQueries: threeCardsLoadingQueries } =
    useUploadContentBlocksImages({
      key: PAGE_THREE_CARDS_BLOCKS,
      moduleId,
      setCardBlockImages: setThreeCardBlockImages,
      contentBlocks,
      enabled: isThreeCardBlockModuleTypeLayout(moduleLayout),
    });

  const { loadingQueries: oneCardsLoadingQueries } =
    useUploadContentBlocksImages({
      key: PAGE_ONE_CARD_BLOCKS,
      moduleId,
      setCardBlockImages: setOneCardBlockImage,
      contentBlocks,
      enabled: isOneCardBlockModuleLayout(moduleLayout),
    });

  const { loadingQuery: topPrivacyTipsBlockImageLoadingQuery } =
    useUploadSingleImage({
      key: MODULE_TOP_PRIVACY_TIPS_BLOCK,
      moduleId,
      setCardBlockImage: setTopPrivacyTipBlockImage,
      backgroundImagePath: topPrivacyTipsBlock.backgroundImage,
      enabled: moduleType === ModuleType.TopPrivacyTips,
    });

  const {
    orderType,
    selectedGames,
    manualSelectedGames,
    setOrderType,
    setSelectedGames,
    setTagSelectedGames,
    setManualSelectedGames,
    clearSelectedGamesData,
    tagSelectedGames,
  } = useSelectedGamesData(
    useShallow((state) => ({
      orderType: state.orderType,
      selectedGames: state.selectedGames,
      manualSelectedGames: state.manualSelectedGames,
      tagSelectedGames: state.tagSelectedGames,
      setOrderType: state.setOrderType,
      setSelectedGames: state.setSelectedGames,
      setTagSelectedGames: state.setTagSelectedGames,
      setManualSelectedGames: state.setManualSelectedGames,
      clearSelectedGamesData: state.clearSelectedGamesData,
    })),
  );

  const { setSelectedStudios, clearStudiosData } = useStudiosData(
    useShallow((state) => ({
      setSelectedStudios: state.setSelectedStudios,
      clearStudiosData: state.clearStudiosData,
    })),
  );

  const { tagBlocks, setTagBlocks, clearTagsData } = useTagBlocksStore(
    useShallow((state) => ({
      tagBlocks: state.tagBlocks,
      setTagBlocks: state.setTagBlocks,
      clearTagsData: state.clearTagsData,
    })),
  );

  const { setOrderedStudios, clearStudioOrderingData } = useStudioOrderingData(
    useShallow((state) => ({
      setOrderedStudios: state.setOrderedStudios,
      clearStudioOrderingData: state.clearStudioOrderingData,
    })),
  );

  const {
    data: selectedGamesList,
    isLoading: isLoadingSelectedGamesList,
    isFetching: isFetchingSelectedGamesList,
  } = useSelectedGamesInModuleQuery({
    moduleId,
    filters: getAlgoliaTagsFilters(tagBlocks),
  });

  const studioIds = useMemo(() => {
    const studiosIds = new Set<number>();
    selectedGames.forEach((game) => {
      if (game.studioId) {
        studiosIds.add(game.studioId);
      }
    });

    return Array.from(studiosIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGames.length]);

  const { data: availableStudios, isLoading: isLoadingAvailableStudios } =
    useModuleSelectedStudiosQuery(studioIds);

  useEffect(() => {
    if (!moduleData) {
      console.warn("Cannot find page module");
      return;
    }
    const orderType = moduleData.studioFilterEnabled
      ? GamesOrderType.Manual
      : moduleData.gamesOrderType || GamesOrderType.Random;
    setModuleName(moduleData.name);
    setAdminNotes(moduleData.adminNotes ?? "");
    setTranslations(moduleData.translations ?? {});
    setModuleType(moduleData.type);
    setModuleLayout(moduleData.layout);
    setOrderType(orderType);
    setSelectedStudios(formatStudios(moduleData, studios));
    setStudioFilterEnabled(moduleData.studioFilterEnabled ?? false);
    setBaseUrl(moduleData.baseUrl ?? "");
    setRows(moduleData.rows ?? 1);
    setContentBlocks(moduleData.contentBlocks);
    setTopPrivacyTipsBlock(
      moduleData.topPrivacyTipsBlock ?? defaultTopPrivacyTipsBlock(),
    );
    setBonusObtainType(moduleData.bonusObtainType ?? null);
    setScheduledStartAt(moduleData.scheduledStartAt ?? "");
    setScheduledEndAt(moduleData.scheduledEndAt ?? "");
    setIsSchedulingEnabled(Boolean(moduleData.scheduledStartAt?.length));

    const tagBlocks = serializeModuleTagsToBlocks({
      moduleTagTypes: moduleData.tagTypes,
      moduleTags: moduleData.tags,
      tags: tags,
      metadataTags: metadataTags,
      themeTags: themeTags,
      tagsMatchRules: moduleData.tagsMatchRules ?? [],
    });

    setTagBlocks(tagBlocks);

    if (moduleData.icon) {
      setIconShown(true);
      setIcon(moduleData.icon);
    }

    // disabled, because we need to track only for loading state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoadingTags,
    isLoadingMetadataTags,
    isLoadingThemeTags,
    isLoadingStudios,
    moduleId,
  ]);

  useEffect(() => {
    if (hasReadAccess) {
      return;
    }

    if (hasReadTranslationsAccess) {
      setSelectedTab(ModuleDetailsTabs.Translations);
    }
  }, [hasReadAccess, hasReadTranslationsAccess, moduleData]);

  useEffect(() => {
    const shouldUpdateGames =
      !isLoadingAllGames &&
      moduleData?.selectedGames.length &&
      !!availableStudios;

    if (shouldUpdateGames) {
      const manualGames = moduleData.selectedGames
        .map((selectedGame: ModuleGame) =>
          allGames.data.find((game) => game.id.toString() === selectedGame.id),
        )
        .filter(Boolean) as Game[];

      const formattedGames = formatSelectedGames(
        manualGames,
        GamesOrderType.Manual,
        availableStudios,
        moduleData?.selectedGames,
      );

      if (!manualSelectedGames.length && !initialRenderRef.current) {
        setManualSelectedGames(formattedGames);
        initialRenderRef.current = true;
      } else if (manualSelectedGames.length) {
        const orderFromModule = formattedGames.map((g) => g.id);
        const orderFromState = manualSelectedGames.map((g) => g.id);
        const orderChanged =
          JSON.stringify(orderFromModule) !== JSON.stringify(orderFromState);

        if (orderChanged) {
          setManualSelectedGames(formattedGames);
        } else {
          const updatedGames = manualSelectedGames.map((game) => {
            const formattedGame = formattedGames.find(
              (formattedGame) => formattedGame.id === game.id,
            );

            if (formattedGame && formattedGame.studioName) {
              return {
                ...game,
                studioName: formattedGame.studioName,
              };
            }
            return game;
          });

          setManualSelectedGames(updatedGames);
        }
      }
    }

    // disabled, because we do not need to track setters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoadingAllGames,
    moduleData?.selectedGames,
    availableStudios?.length,
    initialRenderRef.current,
  ]);

  useEffect(() => {
    if (!manualSelectedGames.length || !availableStudios) return;

    const formattedGames = formatSelectedGames(
      manualSelectedGames
        .map((game) => {
          return allGames.data.find((g) => g.id.toString() === game.id);
        })
        .filter(Boolean) as Game[],
      orderType,
      availableStudios,
      moduleData?.selectedGames,
    );

    setManualSelectedGames(formattedGames);

    // disabled, because we do not need to track setters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType]);

  useEffect(() => {
    const shouldUpdateGames =
      !isLoadingSelectedGamesList && selectedGamesList && !!availableStudios;

    if (shouldUpdateGames) {
      const sortedGames = sortBy(selectedGamesList, (game) => {
        const existingIdx = moduleData?.selectedGames.findIndex(
          (selectedGame: { id: string }) =>
            game.id.toString() === selectedGame.id,
        );

        return existingIdx === -1 ? Infinity : existingIdx;
      });

      const formattedGames = formatSelectedGames(
        sortedGames,
        orderType,
        availableStudios,
        moduleData?.selectedGames,
      );

      setTagSelectedGames(formattedGames);
    }

    // disabled, because we do not need to track setters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderType,
    isLoadingSelectedGamesList,
    selectedGamesList,
    moduleData?.selectedGames,
    availableStudios?.length,
  ]);

  useEffect(() => {
    const combinedGames = [...manualSelectedGames, ...tagSelectedGames];

    const uniqueGames = combinedGames.filter(
      (game, index, self) => index === self.findIndex((g) => g.id === game.id),
    );

    setSelectedGames(uniqueGames);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualSelectedGames, tagSelectedGames]);

  useEffect(() => {
    if (!availableStudios || isLoadingAvailableStudios) {
      return;
    }

    const sortedStudios = sortBy(availableStudios, (studio) => {
      const existingIdx = moduleData?.orderedGamesStudiosIds?.findIndex(
        (studioId: string) => {
          return studio.id.toString() === studioId;
        },
      );

      return existingIdx === -1 ? Infinity : existingIdx;
    });

    const formattedStudios = sortedStudios.map((studio, idx) => {
      const selectedGamesCount = selectedGames.filter(
        (game) => game.studioId === studio.id,
      ).length;

      return {
        id: studio.id.toString(),
        name: studio.name,
        gamesCount: selectedGamesCount,
        order: idx + 1,
      };
    });

    setOrderedStudios(formattedStudios);

    // disabled, because we do not need to track setters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    availableStudios,
    isLoadingAvailableStudios,
    moduleData?.orderedGamesStudiosIds,
    selectedGames,
  ]);

  const handleDrawerClose = () => {
    moduleDetailsDrawer.closeDrawer();
    setSelectedTab(ModuleDetailsTabs.General);
    clearGeneralData();
    clearSelectedGamesData();
    clearStudiosData();
    clearTagsData();
    clearStudioOrderingData();
    initialRenderRef.current = false;
  };

  const value: ModuleDetailsType = {
    allGames: allGames.data,
    moduleId,
    handleDrawerClose,
    selectedTab,
    setSelectedTab,
    moduleData: moduleData ?? null,
    isLoadingSelectedGamesList,
    isFetchingSelectedGamesList,
    isLoadingAvailableStudios,
    availableStudios,
    brandId,
    pageId,
    oneCardsLoadingQueries,
    threeCardsLoadingQueries,
    topPrivacyTipsBlockImageLoadingQuery,
  };

  return <Provider value={value}>{props.children}</Provider>;
};

export {
  ContentScheduleModuleDetailsProvider,
  useContentScheduleModuleDetails,
};
